import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import type { Musteri } from '../hooks/useCustomers';
import { hesaplaBakimDurumu, telefonWhatsApp } from '@/pages/bakim-takip/utils/bakimHesapla';
import { ISLEM_TURLERI, ODEME_TIPLERI, ODEME_DURUMLARI } from '@/pages/servis-kayitlari/hooks/useServisKayitlari';

interface CustomerDetailModalProps {
  customer: Musteri | null;
  onClose: () => void;
  onRefresh: () => void;
}

type Tab = 'genel' | 'cihazlar' | 'servis' | 'bakim' | 'odemeler';

const tabList: { key: Tab; label: string; icon: string }[] = [
  { key: 'genel', label: 'Genel Bilgiler', icon: 'ri-information-line' },
  { key: 'cihazlar', label: 'Cihazlar', icon: 'ri-hard-drive-2-line' },
  { key: 'servis', label: 'Servis Geçmişi', icon: 'ri-tools-line' },
  { key: 'bakim', label: 'Bakım Geçmişi', icon: 'ri-calendar-check-line' },
  { key: 'odemeler', label: 'Ödemeler', icon: 'ri-money-dollar-circle-line' },
];

const durumConfig: Record<string, { label: string; cls: string }> = {
  yesil: { label: 'Bakım Süresi Uygun', cls: 'bg-emerald-100 text-emerald-700' },
  sari: { label: 'Bakım Zamanı Geldi', cls: 'bg-yellow-100 text-yellow-700' },
  turuncu: { label: 'Bakım Gecikiyor', cls: 'bg-orange-100 text-orange-700' },
  kirmizi: { label: 'Kritik Bakım Gecikmesi', cls: 'bg-red-100 text-red-700' },
  yok: { label: 'Bakım Kaydı Yok', cls: 'bg-gray-100 text-gray-500' },
};

const servisDurumRenk: Record<string, string> = {
  'Tamamlandı': 'bg-emerald-100 text-emerald-700',
  'tamamlandi': 'bg-emerald-100 text-emerald-700',
  'Planlandı': 'bg-sky-100 text-sky-700',
  'planlandi': 'bg-sky-100 text-sky-700',
  'Servise Çıkıldı': 'bg-yellow-100 text-yellow-700',
  'yolda': 'bg-amber-100 text-amber-700',
  'İptal Edildi': 'bg-red-100 text-red-700',
  'iptal': 'bg-red-100 text-red-700',
  'Ertelendi': 'bg-orange-100 text-orange-700',
};

const odemeDurumRenk: Record<string, string> = {
  'Ödendi': 'bg-emerald-100 text-emerald-700',
  'odendi': 'bg-emerald-100 text-emerald-700',
  'Kısmi Ödendi': 'bg-yellow-100 text-yellow-700',
  'kismi': 'bg-yellow-100 text-yellow-700',
  'Ödeme Bekliyor': 'bg-red-100 text-red-700',
  'bekliyor': 'bg-red-100 text-red-700',
  'Ücretsiz İşlem': 'bg-gray-100 text-gray-500',
};

interface Cihaz {
  id: string;
  cihaz_turu: string;
  marka: string | null;
  model: string | null;
  seri_numarasi: string | null;
  kurulum_tarihi: string | null;
  cihaz_konumu: string | null;
  bakim_periyodu_ay: number | null;
  aktif: boolean;
  notlar: string | null;
}

interface ServisKaydi {
  id: string;
  servis_tarihi: string;
  servis_saati: string | null;
  islem_turu: string;
  servis_durumu: string | null;
  yapilan_islem_aciklamasi: string | null;
  servis_notu: string | null;
  alinan_ucret: number | null;
  odeme_durumu: string | null;
  odeme_tipi: string | null;
  personel_adi: string | null;
}

interface BakimKaydi {
  id: string;
  son_bakim_tarihi: string | null;
  bakim_periyodu_ay: number | null;
  bir_sonraki_bakim_tarihi: string | null;
  hatirlatma_durumu: string | null;
  sonuc: string | null;
  notlar: string | null;
}

interface OdemeKaydi {
  id: string;
  tutar: number;
  odeme_durumu: string;
  odeme_tipi: string | null;
  odeme_tarihi: string | null;
  aciklama: string | null;
}

/* ─── Hızlı Servis Ekleme Modal ─── */
function HizliServisModal({ musteriId, onClose, onSaved }: { musteriId: string; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    islem_turu: 'Filtre Bakımı – Dörtlü',
    diger_aciklama: '',
    servis_tarihi: new Date().toISOString().split('T')[0],
    servis_saati: '09:00',
    alinan_ucret: '',
    odeme_durumu: 'odendi',
    odeme_tipi: 'Nakit',
    personel_id: '',
    yapilan_islem_aciklamasi: '',
    servis_notu: '',
  });
  const [personeller, setPersoneller] = useState<{ id: string; ad: string; soyad: string }[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase.from('sadem_personeller').select('id, ad, soyad').eq('aktif', true).order('ad').then(({ data }) => {
      if (data) setPersoneller(data);
    }).catch(() => {});
  }, []);

  const saatler = ['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00','17:30','18:00'];
  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-sky-400 bg-white';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { error } = await supabase.from('sadem_servis_kayitlari').insert({
        musteri_id: musteriId,
        islem_turu: form.islem_turu,
        diger_aciklama: form.islem_turu === 'Diğer' ? form.diger_aciklama : '',
        servis_tarihi: form.servis_tarihi,
        servis_saati: form.servis_saati,
        alinan_ucret: form.alinan_ucret ? Number(form.alinan_ucret) : 0,
        odeme_durumu: form.odeme_durumu,
        odeme_tipi: form.odeme_tipi,
        personel_id: form.personel_id || null,
        yapilan_islem_aciklamasi: form.yapilan_islem_aciklamasi,
        servis_notu: form.servis_notu,
        servis_durumu: 'planlandi',
        aktif: true,
      });
      if (error) throw error;
      onSaved();
      onClose();
    } catch (err) {
      console.error('[HizliServis] hata:', err);
      alert('Servis kaydı oluşturulamadı.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-800">Yeni Servis Ekle</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">İşlem Türü</label>
            <select className={inputCls} value={form.islem_turu} onChange={e => setForm(p => ({ ...p, islem_turu: e.target.value }))}>
              {ISLEM_TURLERI.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {form.islem_turu === 'Diğer' && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Diğer Açıklama</label>
              <input className={inputCls} value={form.diger_aciklama} onChange={e => setForm(p => ({ ...p, diger_aciklama: e.target.value }))} placeholder="Açıklayın..." />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Tarih</label>
              <input type="date" className={inputCls} value={form.servis_tarihi} onChange={e => setForm(p => ({ ...p, servis_tarihi: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Saat</label>
              <select className={inputCls} value={form.servis_saati} onChange={e => setForm(p => ({ ...p, servis_saati: e.target.value }))}>
                {saatler.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Ücret (₺)</label>
              <input type="number" min="0" className={inputCls} value={form.alinan_ucret} onChange={e => setForm(p => ({ ...p, alinan_ucret: e.target.value }))} placeholder="0" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Ödeme</label>
              <select className={inputCls} value={form.odeme_durumu} onChange={e => setForm(p => ({ ...p, odeme_durumu: e.target.value }))}>
                {ODEME_DURUMLARI.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Personel</label>
            <select className={inputCls} value={form.personel_id} onChange={e => setForm(p => ({ ...p, personel_id: e.target.value }))}>
              <option value="">Seçiniz...</option>
              {personeller.map(p => <option key={p.id} value={p.id}>{p.ad} {p.soyad}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">İşlem Açıklaması</label>
            <textarea className={`${inputCls} resize-none`} rows={2} value={form.yapilan_islem_aciklamasi} onChange={e => setForm(p => ({ ...p, yapilan_islem_aciklamasi: e.target.value }))} placeholder="Yapılan işlem..." maxLength={500} />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl cursor-pointer text-sm">İptal</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-sky-600 text-white font-semibold rounded-xl cursor-pointer text-sm hover:bg-sky-700 disabled:opacity-50">
              {saving ? 'Kaydediliyor...' : 'Servis Ekle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const SERVIS_DURUM_LABEL: Record<string, string> = {
  planlandi: 'Planlandı',
  yolda: 'Yolda',
  islemde: 'İşlemde',
  tamamlandi: 'Tamamlandı',
  iptal: 'İptal',
};

export default function CustomerDetailModal({ customer, onClose, onRefresh }: CustomerDetailModalProps) {
  const [tab, setTab] = useState<Tab>('genel');
  const [cihazlar, setCihazlar] = useState<Cihaz[]>([]);
  const [servisler, setServisler] = useState<ServisKaydi[]>([]);
  const [bakimlar, setBakimlar] = useState<BakimKaydi[]>([]);
  const [odemeler, setOdemeler] = useState<OdemeKaydi[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [servisModalOpen, setServisModalOpen] = useState(false);
  const navigate = useNavigate();

  const loadDetails = () => {
    if (!customer) return;
    setLoadingDetail(true);
    Promise.all([
      supabase.from('sadem_cihazlar').select('*').eq('musteri_id', customer.id).eq('aktif', true).order('created_at', { ascending: false }),
      supabase.from('sadem_servis_kayitlari').select('id, servis_tarihi, servis_saati, islem_turu, servis_durumu, yapilan_islem_aciklamasi, servis_notu, alinan_ucret, odeme_durumu, odeme_tipi, personel_id').eq('musteri_id', customer.id).eq('aktif', true).order('servis_tarihi', { ascending: false }),
      supabase.from('sadem_bakim_takibi').select('*').eq('musteri_id', customer.id).eq('aktif', true).order('son_bakim_tarihi', { ascending: false }),
      supabase.from('sadem_odemeler').select('*').eq('musteri_id', customer.id).order('odeme_tarihi', { ascending: false }),
    ])
      .then(([cRes, sRes, bRes, oRes]) => {
        if (!cRes.error) setCihazlar(cRes.data as Cihaz[]);
        if (!sRes.error) {
          const servisData = sRes.data as ServisKaydi[];
          const personelIds = [...new Set(servisData.filter(s => s.personel_id).map(s => s.personel_id))];
          if (personelIds.length > 0) {
            supabase.from('sadem_personeller').select('id, ad, soyad').in('id', personelIds).then(pRes => {
              if (!pRes.error) {
                const personelMap: Record<string, string> = {};
                (pRes.data || []).forEach((p: { id: string; ad: string; soyad: string }) => {
                  personelMap[p.id] = `${p.ad} ${p.soyad}`;
                });
                setServisler(servisData.map(s => ({ ...s, personel_adi: s.personel_id ? personelMap[s.personel_id] || null : null })));
              } else {
                setServisler(servisData);
              }
            });
          } else {
            setServisler(servisData);
          }
        }
        if (!bRes.error) setBakimlar(bRes.data as BakimKaydi[]);
        if (!oRes.error) setOdemeler(oRes.data as OdemeKaydi[]);
      })
      .finally(() => setLoadingDetail(false));
  };

  useEffect(() => {
    loadDetails();
  }, [customer]);

  if (!customer) return null;

  const durum = durumConfig[customer.bakim_durumu] || durumConfig.yok;
  const totalHarcama = servisler.reduce((s, sv) => s + (Number(sv.alinan_ucret) || 0), 0);
  const totalOdeme = odemeler.reduce((s, o) => s + (Number(o.tutar) || 0), 0);
  const totalServisSayisi = servisler.length;
  const sonServis = servisler[0];
  const enSonBakim = bakimlar[0];
  const bakimDurumu = hesaplaBakimDurumu(enSonBakim?.son_bakim_tarihi || null);

  const renderGenel = () => (
    <div className="space-y-5">
      <button
        onClick={() => setServisModalOpen(true)}
        className="w-full flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold px-4 py-3 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap mb-2">
        <i className="ri-add-circle-line"></i>
        Yeni İşlem / Servis Ekle
      </button>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-xs text-gray-400 font-medium">Bakım Durumu</p>
          <span className={`text-xs font-bold px-2 py-1 rounded-lg mt-1 inline-block ${durum.cls}`}>{durum.label}</span>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-xs text-gray-400 font-medium">Cihaz Sayısı</p>
          <p className="text-sm font-semibold text-gray-800 mt-1">{customer.cihaz_sayisi} cihaz</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-xs text-gray-400 font-medium">Toplam Harcama</p>
          <p className="text-sm font-semibold text-emerald-600 mt-1">{totalHarcama.toLocaleString('tr-TR')} TL</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-xs text-gray-400 font-medium">Toplam Servis</p>
          <p className="text-sm font-semibold text-gray-800 mt-1">{totalServisSayisi} servis</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-xs text-gray-400 font-medium">Son Bakım</p>
          <p className="text-sm font-semibold text-gray-800 mt-1">
            {enSonBakim?.son_bakim_tarihi ? new Date(enSonBakim.son_bakim_tarihi).toLocaleDateString('tr-TR') : '-'}
          </p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-xs text-gray-400 font-medium">Sonraki Bakım</p>
          <p className="text-sm font-semibold text-gray-800 mt-1">
            {enSonBakim?.bir_sonraki_bakim_tarihi ? new Date(enSonBakim.bir_sonraki_bakim_tarihi).toLocaleDateString('tr-TR') : '-'}
          </p>
        </div>
      </div>

      {sonServis && (
        <div className="bg-sky-50 border border-sky-100 rounded-xl p-4">
          <p className="text-xs text-gray-400 font-medium">Son Yapılan İşlem</p>
          <p className="text-sm font-semibold text-gray-800 mt-1">
            {sonServis.islem_turu} — {new Date(sonServis.servis_tarihi).toLocaleDateString('tr-TR')}
            {sonServis.personel_adi && ` • ${sonServis.personel_adi}`}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-gray-400 font-medium mb-1">Telefon</p>
          <p className="text-sm text-gray-700 bg-slate-50 rounded-xl p-3">{customer.telefon}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 font-medium mb-1">Alternatif Telefon</p>
          <p className="text-sm text-gray-700 bg-slate-50 rounded-xl p-3">{customer.alternatif_telefon || '-'}</p>
        </div>
      </div>

      {customer.acik_adres && (
        <div>
          <p className="text-xs text-gray-400 font-medium mb-1">Açık Adres</p>
          <p className="text-sm text-gray-700 bg-slate-50 rounded-xl p-3">{customer.acik_adres}, {customer.il}/{customer.ilce}</p>
        </div>
      )}

      {customer.adres_tarifi && (
        <div>
          <p className="text-xs text-gray-400 font-medium mb-1">Adres Tarifi</p>
          <p className="text-sm text-gray-700 bg-slate-50 rounded-xl p-3">{customer.adres_tarifi}</p>
        </div>
      )}

      {customer.google_maps_url && (
        <div>
          <p className="text-xs text-gray-400 font-medium mb-1">Konum</p>
          <a href={customer.google_maps_url} target="_blank" rel="noopener noreferrer" className="text-sm text-sky-600 hover:text-sky-700 underline">
            Google Haritalar'da Görüntüle
          </a>
        </div>
      )}

      <div>
        <p className="text-xs text-gray-400 font-medium mb-1">Müşteri Durumu</p>
        <p className="text-sm text-gray-700 bg-slate-50 rounded-xl p-3">{customer.musteri_durumu || '-'}</p>
      </div>

      {customer.genel_notlar && (
        <div>
          <p className="text-xs text-gray-400 font-medium mb-1">Genel Notlar</p>
          <p className="text-sm text-gray-700 bg-yellow-50 border border-yellow-100 rounded-xl p-3">{customer.genel_notlar}</p>
        </div>
      )}
    </div>
  );

  const renderCihazlar = () => (
    <div>
      {cihazlar.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-10">Bu müşteriye ait cihaz kaydı bulunmuyor.</p>
      ) : (
        <div className="space-y-3">
          {cihazlar.map(c => (
            <div key={c.id} className="border border-gray-100 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-800">{c.cihaz_turu}</p>
                  {(c.marka || c.model) && <p className="text-xs text-gray-500 mt-0.5">{[c.marka, c.model].filter(Boolean).join(' ')}</p>}
                </div>
                {!c.aktif && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">Pasif</span>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 text-xs">
                {c.seri_numarasi && <div><span className="text-gray-400">Seri No:</span><span className="text-gray-700 ml-1">{c.seri_numarasi}</span></div>}
                {c.kurulum_tarihi && <div><span className="text-gray-400">Kurulum:</span><span className="text-gray-700 ml-1">{new Date(c.kurulum_tarihi).toLocaleDateString('tr-TR')}</span></div>}
                {c.bakim_periyodu_ay && <div><span className="text-gray-400">Bakım Periyodu:</span><span className="text-gray-700 ml-1">{c.bakim_periyodu_ay} ay</span></div>}
                {c.cihaz_konumu && <div><span className="text-gray-400">Konum:</span><span className="text-gray-700 ml-1">{c.cihaz_konumu}</span></div>}
              </div>
              {c.notlar && <p className="text-xs text-gray-500 mt-2 italic">{c.notlar}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderServisler = () => (
    <div>
      <button
        onClick={() => setServisModalOpen(true)}
        className="w-full flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold px-4 py-3 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap mb-4">
        <i className="ri-add-circle-line"></i>
        Yeni İşlem / Servis Ekle
      </button>
      {servisler.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-10">Henüz servis kaydı bulunmuyor.</p>
      ) : (
        <div className="space-y-3">
          {servisler.map(s => (
            <div key={s.id} className="border border-gray-100 rounded-xl p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-800">{s.islem_turu}</p>
                    {s.servis_durumu && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${servisDurumRenk[s.servis_durumu] || 'bg-gray-100 text-gray-600'}`}>
                        {SERVIS_DURUM_LABEL[s.servis_durumu] || s.servis_durumu}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(s.servis_tarihi).toLocaleDateString('tr-TR')}
                    {s.servis_saati && ` • ${s.servis_saati}`}
                    {s.personel_adi && ` • ${s.personel_adi}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-emerald-600">
                    {s.alinan_ucret ? `${Number(s.alinan_ucret).toLocaleString('tr-TR')} TL` : '-'}
                  </p>
                  {s.odeme_durumu && (
                    <span className={`text-xs ${odemeDurumRenk[s.odeme_durumu] || 'text-gray-600'} px-1.5 py-0.5 rounded-full`}>
                      {s.odeme_durumu}
                    </span>
                  )}
                </div>
              </div>
              {s.yapilan_islem_aciklamasi && (
                <p className="text-xs text-gray-600 mt-2 bg-slate-50 rounded-lg p-2">{s.yapilan_islem_aciklamasi}</p>
              )}
              {s.servis_notu && <p className="text-xs text-gray-500 mt-1 italic">Not: {s.servis_notu}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderBakimlar = () => (
    <div>
      {bakimlar.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-10">Henüz bakım kaydı bulunmuyor.</p>
      ) : (
        <div className="space-y-3">
          {bakimlar.map(b => {
            const bDurum = hesaplaBakimDurumu(b.son_bakim_tarihi);
            const bDurumCls = durumConfig[bDurum]?.cls || 'bg-gray-100 text-gray-500';
            return (
              <div key={b.id} className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      Son Bakım: {b.son_bakim_tarihi ? new Date(b.son_bakim_tarihi).toLocaleDateString('tr-TR') : '-'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Periyot: {b.bakim_periyodu_ay ?? '?'} ay • Sonraki: {b.bir_sonraki_bakim_tarihi ? new Date(b.bir_sonraki_bakim_tarihi).toLocaleDateString('tr-TR') : '-'}
                    </p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${bDurumCls}`}>
                    {durumConfig[bDurum]?.label || 'Bilinmiyor'}
                  </span>
                </div>
                {(b.sonuc || b.notlar) && (
                  <p className="text-xs text-gray-600 mt-2 bg-slate-50 rounded-lg p-2">
                    {[b.sonuc, b.notlar].filter(Boolean).join(' • ')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderOdemeler = () => (
    <div>
      {odemeler.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-10">Henüz ödeme kaydı bulunmuyor.</p>
      ) : (
        <div className="space-y-3">
          {odemeler.map(o => (
            <div key={o.id} className="border border-gray-100 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-800">{Number(o.tutar).toLocaleString('tr-TR')} TL</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {o.odeme_tarihi ? new Date(o.odeme_tarihi).toLocaleDateString('tr-TR') : '-'}
                    {o.odeme_tipi && ` • ${o.odeme_tipi}`}
                  </p>
                </div>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${odemeDurumRenk[o.odeme_durumu] || 'bg-gray-100 text-gray-600'}`}>
                  {o.odeme_durumu}
                </span>
              </div>
              {o.aciklama && <p className="text-xs text-gray-500 mt-2">{o.aciklama}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-bold text-gray-800">{customer.ad} {customer.soyad}</h3>
                <span className={`text-xs font-bold px-2 py-1 rounded-lg ${durum.cls}`}>{durum.label}</span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">{customer.telefon} • {customer.il} / {customer.ilce}</p>
            </div>
            <div className="flex items-center gap-2">
              <a href={`https://wa.me/${telefonWhatsApp(customer.telefon)}`} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 cursor-pointer" title="WhatsApp">
                <i className="ri-whatsapp-line"></i>
              </a>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>

          <div className="flex border-b border-gray-100 shrink-0 overflow-x-auto">
            {tabList.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors border-b-2
                  ${tab === t.key ? 'text-sky-600 border-sky-600' : 'text-gray-400 border-transparent hover:text-gray-600'}`}>
                <i className={`${t.icon} text-sm`}></i>
                {t.label}
              </button>
            ))}
          </div>

          <div className="px-6 py-5 overflow-y-auto flex-1">
            {loadingDetail ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-8 h-8 border-3 border-sky-200 border-t-sky-500 rounded-full animate-spin mb-3"></div>
                <p className="text-sm text-gray-400">Yükleniyor...</p>
              </div>
            ) : (
              <>
                {tab === 'genel' && renderGenel()}
                {tab === 'cihazlar' && renderCihazlar()}
                {tab === 'servis' && renderServisler()}
                {tab === 'bakim' && renderBakimlar()}
                {tab === 'odemeler' && renderOdemeler()}
              </>
            )}
          </div>
        </div>
      </div>

      {servisModalOpen && (
        <HizliServisModal
          musteriId={customer.id}
          onClose={() => setServisModalOpen(false)}
          onSaved={() => {
            loadDetails();
            onRefresh();
          }}
        />
      )}
    </>
  );
}